// src/pages/LitOverviewSection.jsx
//
// Student-facing render of an OVERVIEW lit_section. Faithful port of the prototype
// LitOverviewView (between-the-lines-v2.html L12404-12463): a 3-tab view --
// Context / Plot Summary / Structure Diagram -- using the prototype's real markup/classes
// (NOT the simplified admin LitSectionPreview).
//
// Reads the lit_section content's flattened blocks (as the admin LitOverviewEditor saves
// them): blocks.context (paragraph), blocks.keyFacts ([{label,value}]), blocks.plot
// ([{act,summary}]), blocks.structure ([{stage,act,notes}]), blocks.historicalContext
// ([{title,point,linkToText}] -- point-form AO3 cards; linkToText optional plain-text ref).
//
// Pure presentational (takes `content` directly) so the student reader and a future
// admin-preview refactor can share it. No data fetch / gating / glossary highlighting
// (glossary + Export PDF are post-launch).
import React, { useState } from 'react';
import AnnotatedText from '../components/AnnotatedText.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

// PA5: the sub-tab labels + the structure-tab hint are configurable so the SAME reader
// serves literature (Context / Plot Summary / Structure Diagram) and poetry (Anthology
// Context / Anthology Map / Exam-Comparison Strategy). Block DATA keys are unchanged;
// only the DISPLAY strings differ. Defaults reproduce the literature reader exactly.
const DEFAULT_LABELS = {
    context: 'Context',
    plot: 'Plot Summary',
    structure: 'Structure Diagram',
    historicalContext: 'Historical Context',
    structureHint: 'A staged dramatic arc.',
};

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function LitOverviewSection({ content, done = false, labels }) {
    const L = { ...DEFAULT_LABELS, ...(labels || {}) };
    const TABS = [
        ['context', L.context],
        ['plot', L.plot],
        ['structure', L.structure],
        ['historicalContext', L.historicalContext],
    ];
    const blocks = (content && content.blocks) || {};
    const context = typeof blocks.context === 'string' ? blocks.context : '';
    const keyFacts = Array.isArray(blocks.keyFacts) ? blocks.keyFacts : [];
    const plot = Array.isArray(blocks.plot) ? blocks.plot : [];
    const structure = Array.isArray(blocks.structure) ? blocks.structure : [];
    const historicalContext = Array.isArray(blocks.historicalContext) ? blocks.historicalContext : [];
    const [tab, setTab] = useState('context');

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Overview" title={(content && content.title) || 'Overview'}
                blocks={blocks} done={done} />

            <div className="inline-flex border border-book-accent/40 mb-6 rounded-sm overflow-hidden">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${tab === k ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === 'context' && (
                <div className="space-y-5">
                    {context && <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{context}</AnnotatedText></p>}
                    {keyFacts.length > 0 && (
                        <div className="grid md:grid-cols-2 gap-3">
                            {keyFacts.map((p, i) => (
                                <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                    <div className="font-display text-book-muted text-xs mb-1 font-semibold">{p && p.label}</div>
                                    <div className="font-serif text-book-text">{p && p.value}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {!context && keyFacts.length === 0 && <EmptyNote />}
                </div>
            )}

            {tab === 'plot' && (
                plot.length > 0 ? (
                    <div className="space-y-3">
                        {plot.map((p, i) => (
                            <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">{p && p.act}</div>
                                <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{p && p.summary}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'structure' && (
                structure.length > 0 ? (
                    <div className="space-y-3">
                        {L.structureHint && <p className="font-serif italic text-book-muted mb-3">{L.structureHint}</p>}
                        {structure.map((s, i) => (
                            <div key={i} className="flex items-stretch gap-4">
                                <div className="w-32 flex-shrink-0 border border-book-accent/40 bg-book-accent/10 flex items-center justify-center py-3">
                                    <span className="font-display text-book-accent text-xs text-center px-2 font-semibold">{s && s.stage}</span>
                                </div>
                                <div className="flex-grow border-l-2 border-book-accent/30 pl-4 py-1 flex flex-col justify-center">
                                    <div className="font-display text-book-muted text-xs mb-0.5 font-semibold">{s && s.act}</div>
                                    <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{s && s.notes}</AnnotatedText></p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'historicalContext' && (
                historicalContext.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-3">
                        {historicalContext.map((h, i) => {
                            const link = h && typeof h.linkToText === 'string' ? h.linkToText.trim() : '';
                            return (
                                <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 flex flex-col">
                                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">{h && h.title}</div>
                                    <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{h && h.point}</AnnotatedText></p>
                                    {link && (
                                        <div className="mt-3 pt-3 border-t border-book-accent/20">
                                            <div className="font-display text-book-muted text-xs mb-0.5 font-semibold">Link to text</div>
                                            <div className="font-serif italic text-book-muted text-sm">{link}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : <EmptyNote />
            )}
        </div>
    );
}
